import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import {
  RcsAccountManager,
  RcsAgentStage,
  RcsMetrics,
  RcsPingooCandidates,
  RcsQueueAgent,
  RcsRoutePreviewRow,
  RcsTrackingDetail,
  RcsTrackingFilters,
  RcsTrackingNote,
  RcsTrackingNotification,
  RcsTrackingResponse,
  unwrapList,
} from './admin-rcs.models';

/**
 * Rotas do Admin do RCS (`/api/admin/rcs`, `just.admin.can.access`), como estão
 * no `RCS-CONTRACT.md`.
 *
 * Toda transição de etapa é um POST próprio, e não um PATCH com `stage`: cada
 * uma tem regra diferente (registrar `sender_id` move a etapa só em
 * `technical_review`; em `active` só acrescenta o broker), grava histórico e
 * notifica o cliente. Um `setStage()` genérico aqui esconderia isso.
 */
@Injectable({ providedIn: 'root' })
export class AdminRcsService {

  private readonly baseUrl = `${environment.apiUrl}/admin/rcs`;

  constructor(private http: HttpClient) {}

  /** Fila de agentes. Sem `stage` vêm todos, que é o que os contadores precisam. */
  listAgents(stage?: RcsAgentStage): Observable<RcsQueueAgent[]> {
    const params = stage ? new HttpParams().set('stage', stage) : undefined;

    return this.http
      .get<RcsQueueAgent[] | { data: RcsQueueAgent[] }>(`${this.baseUrl}/agents`, { params })
      .pipe(map((response) => unwrapList(response)));
  }

  /** submitted → technical_review. */
  // Transições com `no-toast`: a fila mostra o erro sozinha (no campo, quando o 422 é do
  // `sender_id`; em toast, nos outros). Sem isso o interceptor e a fila davam dois toasts iguais.
  startAnalysis(agentId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/agents/${agentId}/start-analysis?no-toast=1`, {});
  }

  /**
   * Registra a marca num broker. Em `technical_review` leva a `carrier_review`;
   * em `active` só acrescenta o broker às rotas.
   */
  addSender(agentId: string, providerAccountId: string, senderId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/agents/${agentId}/senders?no-toast=1`, {
      provider_account_id: providerAccountId,
      sender_id: senderId,
    });
  }

  // --- Criação automática na Pingoo e vínculo (subtask 17tm466v1y7) --------------------
  // Todas devolvem o detalhe do Acompanhamento; `no-toast`: o painel mostra o erro.

  /** "Aprovar e criar na Pingoo": multipart com o documento da empresa (e a prévia, opcional). */
  pingooCreate(agentId: string, document?: File | null, preview?: File | null): Observable<RcsTrackingDetail> {
    const body = new FormData();
    if (document) { body.append('document', document); }
    if (preview) { body.append('preview', preview); }

    return this.http.post<RcsTrackingDetail>(`${this.baseUrl}/agents/${agentId}/pingoo/create?no-toast=1`, body);
  }

  /** "Tentar de novo" depois de "Erro ao criar na Pingoo". */
  pingooRetry(agentId: string): Observable<RcsTrackingDetail> {
    return this.http.post<RcsTrackingDetail>(`${this.baseUrl}/agents/${agentId}/pingoo/retry?no-toast=1`, {});
  }

  /** Agentes da conta Pingoo ainda sem dono aqui (só leitura na Pingoo). */
  pingooCandidates(agentId: string): Observable<RcsPingooCandidates> {
    return this.http.get<RcsPingooCandidates>(`${this.baseUrl}/agents/${agentId}/pingoo/candidates`);
  }

  /** "Vincular agente da Pingoo": grava o UUID escolhido e sincroniza na hora. */
  pingooLink(agentId: string, pingooAgentId: string): Observable<RcsTrackingDetail> {
    return this.http.post<RcsTrackingDetail>(`${this.baseUrl}/agents/${agentId}/pingoo/link?no-toast=1`, { agent_id: pingooAgentId });
  }

  activate(agentId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/agents/${agentId}/activate?no-toast=1`, {});
  }

  /** O cliente lê `reason` na tela dele: é o que ele precisa corrigir. */
  reject(agentId: string, reason: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/agents/${agentId}/reject?no-toast=1`, { reason });
  }

  suspend(agentId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/agents/${agentId}/suspend?no-toast=1`, {});
  }

  reactivate(agentId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/agents/${agentId}/reactivate?no-toast=1`, {});
  }

  /** `month` no formato `YYYY-MM`; sem ele o backend usa o mês corrente. */
  metrics(month?: string): Observable<RcsMetrics> {
    const params = month ? new HttpParams().set('month', month) : undefined;

    return this.http.get<RcsMetrics>(`${this.baseUrl}/metrics`, { params });
  }

  /** Por onde o RCS de um cliente sai hoje, com as rotas SALVAS dele. */
  routePreview(userId: string): Observable<RcsRoutePreviewRow[]> {
    const params = new HttpParams().set('user_id', userId);

    return this.http
      .get<RcsRoutePreviewRow[] | { data: RcsRoutePreviewRow[] }>(`${this.baseUrl}/route-preview`, { params })
      .pipe(map((response) => unwrapList(response)));
  }

  // --- Acompanhamento de agentes (17tm466frtx) ----------------------------------

  /** `GET /admin/rcs/tracking`. Sem `page`/`per_page` nos filtros, o backend usa o padrão (1 · 50). */
  tracking(filters: RcsTrackingFilters = {}): Observable<RcsTrackingResponse> {
    return this.http.get<RcsTrackingResponse>(`${this.baseUrl}/tracking`, { params: trackingParams(filters) });
  }

  /** Mesmos filtros do `tracking()`, sem paginação: `format=csv` devolve o arquivo (`;` + BOM UTF-8). */
  exportTrackingCsv(filters: RcsTrackingFilters = {}): Observable<Blob> {
    const params = trackingParams({ ...filters, page: undefined, per_page: undefined }).set('format', 'csv');

    return this.http.get(`${this.baseUrl}/tracking`, { params, responseType: 'blob' });
  }

  trackingDetail(agentId: string): Observable<RcsTrackingDetail> {
    return this.http.get<RcsTrackingDetail>(`${this.baseUrl}/tracking/${agentId}`);
  }

  /** `channel` whatsapp/sms sempre volta 422 ("canal ainda não disponível"): sem número oficial (decisão do Bruno). */
  notify(agentId: string, channel: 'email' | 'panel' | 'whatsapp' | 'sms', message?: string): Observable<RcsTrackingNotification> {
    return this.http.post<RcsTrackingNotification>(`${this.baseUrl}/agents/${agentId}/notify?no-toast=1`, { channel, message });
  }

  addNote(agentId: string, body: string): Observable<RcsTrackingNote> {
    return this.http.post<RcsTrackingNote>(`${this.baseUrl}/agents/${agentId}/notes`, { body });
  }

  /** Ligação registrada: grava nota (`kind=call`) e notificação `channel=phone`. */
  addContact(agentId: string, note?: string): Observable<RcsTrackingNote> {
    return this.http.post<RcsTrackingNote>(`${this.baseUrl}/agents/${agentId}/contacts`, { note });
  }

  /** Sync na hora; trava de 5 min por agente → 429 (mensagem amigável no corpo). */
  syncAgent(agentId: string): Observable<RcsTrackingDetail> {
    return this.http.post<RcsTrackingDetail>(`${this.baseUrl}/agents/${agentId}/sync?no-toast=1`, {});
  }

  /** Igual ao `POST /rcs/agents/{id}/resend-verification` do cliente, mas `sent_by` = admin. 429 em < 24h. */
  resendVerification(agentId: string): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/agents/${agentId}/resend-verification?no-toast=1`, {});
  }

  accountManagers(): Observable<RcsAccountManager[]> {
    return this.http
      .get<RcsAccountManager[] | { data: RcsAccountManager[] }>(`${this.baseUrl}/account-managers`)
      .pipe(map((response) => unwrapList(response)));
  }

  /** `null` tira o responsável (cai nos admins do RCS, como hoje). */
  setAccountManager(userId: string, accountManagerId: string | null): Observable<unknown> {
    return this.http.put(`${this.baseUrl}/clients/${userId}/account-manager`, { account_manager_id: accountManagerId });
  }
}

/** Monta os `HttpParams` do `/tracking`, pulando filtro vazio/undefined. */
export function trackingParams(filters: RcsTrackingFilters): HttpParams {
  let params = new HttpParams();

  const entries: [string, unknown][] = [
    ['q', filters.q],
    ['step', filters.step],
    ['client_id', filters.client_id],
    ['use_case', filters.use_case],
    ['carrier', filters.carrier],
    ['carrier_state', filters.carrier_state],
    ['stuck_days', filters.stuck_days],
    ['account_manager_id', filters.account_manager_id],
    ['from', filters.from],
    ['to', filters.to],
    ['sort', filters.sort],
    ['group', filters.group],
    ['page', filters.page],
    ['per_page', filters.per_page],
  ];

  entries.forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params = params.set(key, String(value));
    }
  });

  return params;
}

/**
 * Mensagem para o toast a partir do erro do backend. O contrato promete
 * mensagens em português; quando o corpo não traz nenhuma, cai no texto de
 * quem chamou, que sabe qual ação falhou.
 */
export function rcsErrorMessage(error: HttpErrorResponse | null | undefined, fallback: string): string {
  if (!error) {
    return fallback;
  }

  if (error.status === 403) {
    return 'Só administrador pode mexer no RCS.';
  }

  const body = error.error;

  if (body && typeof body === 'object') {
    // 422 do Laravel: a primeira mensagem de campo diz mais que o "The given data…".
    if (body.errors && typeof body.errors === 'object') {
      const first = Object.values(body.errors as Record<string, string[] | string>)[0];
      const text = Array.isArray(first) ? first[0] : first;

      if (text) {
        return String(text);
      }
    }

    if (typeof body.message === 'string' && body.message.trim() !== '') {
      return body.message;
    }
  }

  if (error.status === 0) {
    return 'Sem conexão com o servidor. Tente de novo.';
  }

  // 429: trava de frequência (sync a cada 5 min, reenvio a cada 24h...). O corpo já costuma
  // trazer a mensagem amigável do contrato; isto é só a rede de segurança.
  if (error.status === 429) {
    return 'Essa ação tem um limite de frequência e foi usada há pouco. Tente de novo mais tarde.';
  }

  return fallback;
}
